import { memo, useMemo, useCallback, useEffect, useState } from 'react'
import { CobuText, CobuView } from '@cobu/ui'
import type { Group } from '@cobu/api-client'
import GroupCard from './GroupCard'
import SuggestGroupCard from './SuggestGroupCard'

const LEFT_HEIGHTS = [215, 125, 165, 115]
const RIGHT_HEIGHTS = [100, 190, 112, 215]
const SPACING = 17
const MOBILE_BREAKPOINT = 768

const getMetaExtraHeight = (group?: Group, isMobile?: boolean) => {
  const typed = group as GroupWithCounts | undefined
  const memberCount = typed?.meta?.groupMemberCount ?? 0
  const eventCount = typed?.meta?.upcomingGroupEventCount ?? 0
  if (memberCount <= 0 && eventCount <= 0) return 0
  return isMobile ? 40 : 50
}

type ProcessedItem = Partial<Group> & {
  type?: 'suggestion'
  height: number
  uuid?: string
  column?: 'left' | 'right'
}

type GroupWithCounts = Group & {
  meta?: Group['meta'] & {
    groupMemberCount?: number
    upcomingGroupEventCount?: number
  }
}

function layoutPanels(groups: Group[], showSuggestCard: boolean, suggestIndex: number, isMobile: boolean) {
  const panels = [...groups]
  const size = panels.length
  const midpoint = size / 2
  let iterator = 0
  const heights = { left: 0, right: 0 }
  let actualIdx = suggestIndex
  let suggestPrecounted = false
  if (showSuggestCard) {
    if (suggestIndex > size - 1) {
      actualIdx = 0
      const firstH = LEFT_HEIGHTS[0]
      heights.left += firstH + SPACING
      suggestPrecounted = true
      if (panels.length === 1) {
        const firstHeightWithExtras = firstH + getMetaExtraHeight(panels[0], isMobile)
        heights.right = firstHeightWithExtras + SPACING
        const item: ProcessedItem = { ...panels[0], height: firstHeightWithExtras, column: 'left' }
        const suggest: ProcessedItem = { type: 'suggestion', height: firstH, column: 'left' }
        return { allItems: [suggest, item], maxHeight: heights.left + 5 }
      }
    }
  }

  const items: ProcessedItem[] = panels.map((g, index) => {
    const item: ProcessedItem = { ...g, height: 0 }
    if (showSuggestCard && actualIdx === index && !suggestPrecounted) {
      const h = iterator < midpoint ? LEFT_HEIGHTS[iterator % LEFT_HEIGHTS.length] : RIGHT_HEIGHTS[iterator % RIGHT_HEIGHTS.length]
      if (iterator < midpoint) heights.left += h + SPACING
      else heights.right += h + SPACING
    }
    if (iterator < midpoint) {
      const base = LEFT_HEIGHTS[iterator % LEFT_HEIGHTS.length]
      const h = base + getMetaExtraHeight(g, isMobile)
      item.height = h
      item.column = 'left'
      heights.left += h + SPACING
    } else if (iterator >= midpoint && iterator !== size - 1) {
      const base = RIGHT_HEIGHTS[iterator % RIGHT_HEIGHTS.length]
      const h = base + getMetaExtraHeight(g, isMobile)
      item.height = h
      item.column = 'right'
      heights.right += h + SPACING
    } else {
      const extra = getMetaExtraHeight(g, isMobile)
      const diff = heights.left - SPACING - heights.right
      const baseFromDiff = diff - extra
      const base = baseFromDiff > 0 ? baseFromDiff : RIGHT_HEIGHTS[0]
      const h = base + extra
      item.height = h
      item.column = 'right'
      heights.right += h + SPACING
    }
    iterator += 1
    return item
  })

  let allItems: ProcessedItem[] = [...items]
  if (showSuggestCard) {
    const suggestHeight = actualIdx < midpoint ? LEFT_HEIGHTS[actualIdx % LEFT_HEIGHTS.length] : RIGHT_HEIGHTS[actualIdx % RIGHT_HEIGHTS.length]
    const suggest: ProcessedItem = { type: 'suggestion', height: suggestHeight, column: actualIdx < midpoint ? 'left' : 'right' }
    if (actualIdx > items.length - 1) allItems.unshift(suggest)
    else allItems.splice(actualIdx, 0, suggest)
  }

  return { allItems, maxHeight: heights.left + 5 }
}

export default memo(function GroupsGrid({
  groups,
  title,
  subtitle,
  onGroupPress,
  onSuggestPress,
  showSuggestCard = false,
  suggestCardPosition = 2,
}: {
  groups: Group[]
  title: string
  subtitle?: string
  onGroupPress: (id: string) => void
  onSuggestPress?: () => void
  showSuggestCard?: boolean
  suggestCardPosition?: number
}) {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < MOBILE_BREAKPOINT
  })

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') return
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const calc = useCallback(() => layoutPanels(groups, !!showSuggestCard, suggestCardPosition, isMobile), [groups, showSuggestCard, suggestCardPosition, isMobile])
  const { allItems } = useMemo(() => calc(), [calc])

  const leftColumn = useMemo(() => allItems.filter((i) => i.column === 'left'), [allItems])
  const rightColumn = useMemo(() => allItems.filter((i) => i.column === 'right'), [allItems])

  const renderColumn = useCallback((items: ProcessedItem[], key: string) => {
    return (
      <CobuView key={key} flex={1} flexGrow={1} flexShrink={1} flexBasis={0} gap={SPACING} minWidth={0}>
        {items.map((item, index) => {
          const k = item.type === 'suggestion' ? `suggest-${index}` : item.uuid
          return (
            <CobuView 
              key={k} 
              paddingLeft="$1" 
              paddingRight="$1" 
              marginLeft="$1" 
              marginRight="$1"
              minWidth={0}
              width="100%"
            >
              {item.type === 'suggestion' ? (
                <SuggestGroupCard onPress={onSuggestPress!} height={item.height!} />
              ) : (
                <GroupCard group={item as Group} height={item.height!} onPress={onGroupPress} />
              )}
            </CobuView>
          )
        })}
      </CobuView>
    )
  }, [onGroupPress, onSuggestPress])

  if (groups.length === 0 && !showSuggestCard) return null

  return (
    <CobuView gap="$3" marginBottom="$4">
      <CobuView paddingHorizontal="$4" paddingTop="$4" gap="$1">
        <CobuText variant="subheading" fow="$semibold">{title}</CobuText>
        {subtitle ? <CobuText variant="body" color="$colorSecondary">{subtitle}</CobuText> : null}
      </CobuView>
      <CobuView paddingHorizontal="$2">
        <CobuView gap="$2" flexDirection="row" minWidth={0} width="100%">
          {renderColumn(leftColumn, 'left')}
          {renderColumn(rightColumn, 'right')}
        </CobuView>
      </CobuView>
    </CobuView>
  )
})


